import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, FileSpreadsheet, FileText, Loader2, Upload, Wallet } from 'lucide-react';
import Alert from '../common/Alert';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ListPageHeader from '../common/ListPageHeader.js';
import SettlementFilterBar from '../common/SettlementFilterBar.js';
import SettlementModalShell from '../common/SettlementModalShell.js';
import CompanySettlementBatchView from '../common/CompanySettlementBatchView.js';
import CompanySettlementDueView from '../common/CompanySettlementDueView.js';
import PaymentProofModal from '../common/PaymentProofModal.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { filterExportButtonClass, filterExportPairClass, formInputClass, formSelectClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatIndianDate, formatIstDateTime, getCurrentMonthRange } from '../../utils/localDate.js';
import { hasPaidAmountSettlementAccess, hasLocationHubBookingAccess } from '../../utils/networkMode.js';
import { useAuth } from '../../context/AuthContext.js';
import companySettlementService from '../../services/companySettlementService.js';
import { PAYMENT_MODES, aiReviewLabel, statusBadge, statusLabel } from '../paid-amount-settlement/paidAmountSettlementUi.js';

const REQUEST_TABS = [
  { id: 'pending', label: 'Pending', statusParam: 'OCR_PENDING,HUMAN_REVIEW,PENDING' },
  { id: 'approved', label: 'Approved', statusParam: 'APPROVED' },
  { id: 'rejected', label: 'Rejected', statusParam: 'REJECTED' },
];

const dateParams = (fromDate, toDate) => {
  const params = {};
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  return params;
};

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

const commissionInsideToPay = (items) => (items || []).reduce((sum, item) => {
  if (String(item.commission_status || '').toUpperCase() === 'PAID') return sum;
  const gross = Number(item.gross_to_pay ?? item.to_pay_freight);
  if (!Number.isFinite(gross) || gross <= 0) return sum;
  const kept = Number(item.net_amount ?? item.commission_amount ?? 0);
  return sum + Math.min(Math.max(kept, 0), gross);
}, 0);

const displaySettlement = (data) => {
  const pas = Number(data?.paid?.amount_outstanding || 0);
  const twoP = Number(data?.to_pay?.amount_outstanding || 0);
  const commission = Number(data?.unpaid_commission || 0);
  const items = data?.to_pay?.items || [];
  const itemsGross = roundMoney(items.reduce((sum, item) => {
    const value = Number(item.gross_to_pay ?? item.to_pay_freight);
    return sum + (Number.isFinite(value) && value > 0 ? value : 0);
  }, 0));
  // Prefer backend gross (includes sheets fully covered by commission).
  const gross = roundMoney(Number(data?.to_pay_gross) > 0 ? data.to_pay_gross : itemsGross);
  const takenFromItems = roundMoney(commissionInsideToPay(items));
  const takenFromBackend = roundMoney(Number(data?.commission_already_deducted) || 0);
  const taken = Math.max(takenFromItems, takenFromBackend);
  const extra = Math.max(0, roundMoney(commission - taken));
  const branchExpense = Number(data?.branch_expense_credit || 0);
  const computedNet = roundMoney(
    pas + twoP - extra - Number(data?.indent_credit || 0) - branchExpense + Number(data?.commission_clawback || 0),
  );
  // Prefer server net when present so banner formula matches Make Settlement.
  const netValue = data?.net_settlement != null && Number.isFinite(Number(data.net_settlement))
    ? roundMoney(data.net_settlement)
    : computedNet;
  return { pas, twoP, commission, gross, taken, extra, branchExpense, netValue };
};

const cashAmounts = (pasDue, twoPDue) => {
  // Always remit full open sheet dues. Do NOT cut To-Pay by "extra" commission —
  // that underpay left leftover outstanding and kept Update Status locked.
  // Excess commission is handled on approve as deduct / company-side, not by
  // short-paying the ML remittance.
  const paid = roundMoney(pasDue);
  const two = roundMoney(twoPDue);
  return {
    paid: paid > 0.009 ? String(paid) : '',
    two: two > 0.009 ? String(two) : '',
  };
};

const AgentCompanySettlementPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const showPaid = hasPaidAmountSettlementAccess(user) && hasLocationHubBookingAccess(user);
  const [fromDate, setFromDate] = useState(() => getCurrentMonthRange().fromDate);
  const [toDate, setToDate] = useState(() => getCurrentMonthRange().toDate);
  const [listTab, setListTab] = useState('to_pay');
  useEffect(() => {
    const view = searchParams.get('view');
    if (view === 'company_paid' || view === 'paid' || view === 'to_pay' || view === 'requests') {
      setListTab(view);
    }
  }, [searchParams]);
  const [requestTab, setRequestTab] = useState('pending');
  const [summary, setSummary] = useState(null);
  const [batches, setBatches] = useState([]);
  const [companyPayments, setCompanyPayments] = useState([]);
  const [proofView, setProofView] = useState(null);
  const [requestCounts, setRequestCounts] = useState({ pending: 0, approved: 0, rejected: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [paidAmount, setPaidAmount] = useState('');
  const [toPayAmount, setToPayAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [paymentRef, setPaymentRef] = useState('');
  const [remarks, setRemarks] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [viewBatch, setViewBatch] = useState(null);
  const [viewDue, setViewDue] = useState(null);

  const loadData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const params = dateParams(fromDate, toDate);
      const statusParam = listTab === 'requests'
        ? (REQUEST_TABS.find((t) => t.id === requestTab)?.statusParam || '')
        : '';
      const [sumRes, batchRes] = await Promise.all([
        companySettlementService.getSummary(params),
        companySettlementService.listBatches({
          ...params,
          ...(statusParam ? { status: statusParam } : {}),
        }),
      ]);
      const data = sumRes?.data || {};
      setSummary(data);
      setBatches(batchRes?.data || []);
      const payments = batchRes?.company_payments || [];
      setCompanyPayments(payments);
      if (payments.length > 0) {
        window.dispatchEvent(new Event('rt-alerts-refresh'));
      }
      setRequestCounts(batchRes?.counts || { pending: 0, approved: 0, rejected: 0, all: 0 });
      if (!silent) {
        const shown = displaySettlement(data);
        const split = cashAmounts(shown.pas, shown.twoP);
        setPaidAmount(split.paid);
        setToPayAmount(split.two);
      }
    } catch (err) {
      if (!silent) {
        Alert.error(err.response?.data?.error || 'Failed to load settlement dues');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [fromDate, toDate, listTab, requestTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const hasAiVerifying = batches.some((b) => b.status === 'OCR_PENDING');
  useEffect(() => {
    if (!hasAiVerifying) return undefined;
    const timer = window.setInterval(() => loadData({ silent: true }), 5000);
    return () => window.clearInterval(timer);
  }, [hasAiVerifying, loadData]);

  const shown = displaySettlement(summary);
  const pasDue = shown.pas;
  const twoPDue = shown.twoP;
  const toPayGross = shown.gross;
  // One commission figure for the UI: net commission kept by the agent
  // (after TDS), used in To-Pay − commission = remit.
  const commissionTakenOff = roundMoney(shown.taken + shown.extra);
  const branchExpense = shown.branchExpense || 0;
  const net = shown.netValue;
  const pendingPaid = Boolean(summary?.paid?.pending_request_id);
  const pendingTwoP = (summary?.to_pay?.items || []).some((item) => item.pending_request_id);
  const paidShipments = summary?.paid?.shipments || [];
  const toPayItems = summary?.to_pay?.items || [];
  const paidDisabled = !showPaid || pasDue <= 0 || pendingPaid;
  const toPayDisabled = twoPDue <= 0 || pendingTwoP;
  // Settle when any open remittance remains (full sheet dues), not only when
  // net_settlement > 0 — net can be ₹0 while sheet outstanding is still open.
  const canSettle = (
    (showPaid && pasDue > 0.009 && !pendingPaid) || (twoPDue > 0.009 && !pendingTwoP)
  );
  const cashRemit = roundMoney(
    (showPaid && !pendingPaid ? Math.max(0, pasDue) : 0) + (!pendingTwoP ? Math.max(0, twoPDue) : 0),
  );

  const viewTabs = useMemo(() => {
    const tabs = [
      { id: 'to_pay', label: 'To-Pay', count: toPayItems.length },
    ];
    if (showPaid) {
      tabs.unshift({ id: 'paid', label: 'Paid', count: paidShipments.length });
    }
    tabs.push({ id: 'requests', label: 'My requests', count: requestCounts.all || 0 });
    tabs.push({
      id: 'company_paid',
      label: 'Company paid',
      count: companyPayments.length,
      flash: companyPayments.length > 0 && listTab !== 'company_paid',
    });
    return tabs;
  }, [showPaid, toPayItems.length, paidShipments.length, requestCounts.all, companyPayments.length, listTab]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!okTypes.includes(file.type)) {
      Alert.error('Please upload a JPG/PNG image screenshot');
      return;
    }
    setReceipt(file);
  };

  const resetForm = () => {
    setReceipt(null);
    setPaymentRef('');
    setRemarks('');
    setPaymentMode('cash');
    const split = cashAmounts(pasDue, twoPDue);
    setPaidAmount(split.paid);
    setToPayAmount(split.two);
  };

  const openSettle = () => {
    if (!canSettle) return;
    resetForm();
    setShowForm(true);
  };

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    const paid = paidDisabled ? 0 : (parseFloat(paidAmount || '0') || 0);
    const twoP = toPayDisabled ? 0 : (parseFloat(toPayAmount || '0') || 0);
    if (paid <= 0 && twoP <= 0) {
      Alert.error('Enter a Paid amount, a To-Pay amount, or both');
      return;
    }
    if (!receipt) {
      Alert.error('Payment screenshot is required');
      return;
    }
    if (pendingPaid && paid > 0) {
      Alert.error('A Paid settlement is already pending review');
      return;
    }
    if (pendingTwoP && twoP > 0) {
      Alert.error('A To-Pay settlement is already pending review');
      return;
    }
    try {
      setSubmitting(true);
      const form = new FormData();
      form.append('paid_amount', showPaid ? String(paid) : '0');
      form.append('to_pay_amount', String(twoP));
      form.append('payment_mode', paymentMode);
      form.append('payment_ref', paymentRef);
      form.append('remarks', remarks);
      form.append('receipt', receipt);
      // Include ₹0-remit sheets (commission absorbed) so approve clears Comm. unpaid.
      const settleSheetIds = (summary?.to_pay?.ml_sheet_ids || toPayItems.map((r) => r.ml_sheet_id))
        .filter(Boolean);
      if (twoP > 0 && settleSheetIds.length) {
        form.append('ml_sheet_ids', JSON.stringify(settleSheetIds));
      }
      const res = await companySettlementService.submitBatch(form);
      Alert.success(res?.message || 'Settlement submitted');
      setShowForm(false);
      resetForm();
      setListTab('requests');
      await loadData();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to submit settlement');
    } finally {
      setSubmitting(false);
    }
  };

  const exportParams = dateParams(fromDate, toDate);

  const downloadExcel = async () => {
    try {
      await companySettlementService.downloadExcel(exportParams);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Excel download failed');
    }
  };

  const downloadPdf = async () => {
    try {
      await companySettlementService.downloadPdf(exportParams);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'PDF download failed');
    }
  };

  const openView = async (rowOrId) => {
    const batchId = typeof rowOrId === 'string' ? rowOrId : rowOrId?.batch_id;
    if (!batchId) return;
    try {
      const res = await companySettlementService.getBatch(batchId);
      setViewDue(null);
      setViewBatch(res?.data || (typeof rowOrId === 'object' ? rowOrId : null));
    } catch (err) {
      if (typeof rowOrId === 'object') setViewBatch(rowOrId);
    }
  };

  const openDue = (kind, row) => {
    let pendingId = row?.pending_request_id || null;
    if (pendingId) {
      const match = batches.find((b) => (
        b.batch_id === pendingId
        || (b.paid_requests || []).some((r) => r.request_id === pendingId)
        || (b.to_pay_requests || []).some((r) => r.request_id === pendingId)
      ));
      if (match?.batch_id) pendingId = match.batch_id;
    }
    const pendingStatus = row.pending_request_status
      || (kind === 'paid' ? summary?.paid?.pending_request_status : null)
      || (kind === 'to_pay' ? summary?.to_pay?.pending_request_status : null);
    setViewDue({
      kind,
      row: {
        ...row,
        pending_request_id: pendingId,
        pending_request_status: pendingStatus,
      },
    });
  };

  const exportButtons = (
    <div>
      <label className="hidden lg:block text-xs font-medium text-transparent mb-1 select-none">Export</label>
      <div className={filterExportPairClass}>
        <Button variant="secondary" className={filterExportButtonClass} onClick={downloadExcel}>
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
        </Button>
        <Button variant="secondary" className={filterExportButtonClass} onClick={downloadPdf}>
          <FileText className="w-4 h-4 mr-1" /> PDF
        </Button>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <ListPageHeader
        title="Settle with Company"
        subtitle="The amount you send is the same as the dispatch sheet: To-Pay minus commission, once. If the company owes you, the accountant pays it from Settlement Approvals."
        icon={Wallet}
        backTo="/agent-dashboard"
      >
        <Button variant="primary" icon={Wallet} disabled={!canSettle} onClick={openSettle} mobileIconOnly>
          Make Settlement
        </Button>
      </ListPageHeader>

      {net < -0.009 && (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          Company owes you {fullCurrency(Math.abs(net))}. Do not send this. The accountant pays it from Settlement Approvals. It then shows as Payment paid on the Daily Branch Report.
        </div>
      )}
      {cashRemit > 0.009 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Send {fullCurrency(cashRemit)} to the company
          {toPayGross > 0
            ? `: To-Pay ${fullCurrency(toPayGross)} − commission ${fullCurrency(commissionTakenOff)} = ${fullCurrency(roundMoney(Math.max(0, toPayGross - commissionTakenOff)))} (open To-Pay ${fullCurrency(twoPDue)})`
            : ''}
          {pasDue > 0.009 ? ` + Paid due ${fullCurrency(pasDue)}` : ''}
          {branchExpense > 0 ? ` − approved expenses ${fullCurrency(branchExpense)}` : ''}
          . After the accountant approves, it shows as Payment received on the Daily Branch Report.
        </div>
      )}

      <SettlementFilterBar
        statusLabel="View"
        tabs={viewTabs}
        activeTab={listTab}
        onTabChange={setListTab}
        fromDate={fromDate}
        toDate={toDate}
        onFromDate={setFromDate}
        onToDate={setToDate}
        extraRight={exportButtons}
      >
        {listTab === 'requests' ? (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <div className="flex flex-wrap gap-1.5 p-1 rounded-xl bg-gray-50 border border-gray-200">
              {REQUEST_TABS.map((tab) => {
                const active = requestTab === tab.id;
                const count = requestCounts[tab.id] ?? 0;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setRequestTab(tab.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
                      active
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/70'
                    }`}
                  >
                    {tab.label}
                    <span
                      className={`min-w-[1.25rem] text-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                        active ? 'bg-blue-50 text-blue-700' : 'bg-gray-200/80 text-gray-600'
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </SettlementFilterBar>

      {loading ? (
        <Loader fullScreen={false} message="Loading dues..." />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
            {showPaid && (
              <div className="bg-white rounded-xl border border-amber-100 p-3">
                <p className="text-xs text-gray-500">Paid due</p>
                <p className="text-lg font-semibold text-amber-800 tabular-nums">{fullCurrency(pasDue)}</p>
              </div>
            )}
            <div className="bg-white rounded-xl border border-sky-100 p-3">
              <p className="text-xs text-gray-500">To-Pay</p>
              <p className="text-lg font-semibold text-sky-800 tabular-nums">{fullCurrency(toPayGross || twoPDue)}</p>
              {commissionTakenOff > 0 && (
                <p className="text-[11px] text-gray-500 mt-0.5">After commission {fullCurrency(twoPDue)}</p>
              )}
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-3">
              <p className="text-xs text-gray-500">Commission</p>
              <p className="text-lg font-semibold text-gray-800 tabular-nums">{fullCurrency(commissionTakenOff)}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Subtracted from To-Pay</p>
            </div>
            <div className="bg-white rounded-xl border border-blue-100 p-3">
              <p className="text-xs text-gray-500">Approved expenses</p>
              <p className="text-lg font-semibold text-blue-800 tabular-nums">{fullCurrency(branchExpense)}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Minus from the amount you return</p>
            </div>
            <div className="bg-white rounded-xl border border-emerald-100 p-3">
              <p className="text-xs text-gray-500">You remit</p>
              <p className="text-lg font-semibold text-emerald-800 tabular-nums">{fullCurrency(cashRemit)}</p>
              <p className="text-[11px] text-gray-500 mt-0.5">Full open Paid + To-Pay dues</p>
            </div>
            <div className="bg-white rounded-xl border border-emerald-100 p-3">
              <p className="text-xs text-gray-500">Company pays you</p>
              <p className="text-lg font-semibold text-emerald-800 tabular-nums">
                {fullCurrency(net < 0 ? Math.abs(net) : 0)}
              </p>
            </div>
          </div>

          {listTab === 'paid' && showPaid && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">Paid remittance due</h2>
              </div>
              {!paidShipments.length ? (
                <p className="text-sm text-gray-400 text-center py-8">No outstanding Paid GCs</p>
              ) : (
                <>
                <div className="hidden xl:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-semibold">GC</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Route</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Paid</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Remitted</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Outstanding</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paidShipments.map((row) => (
                        <tr
                          key={row.shipment_id}
                          className="hover:bg-sky-50/50 cursor-pointer"
                          onClick={() => openDue('paid', { ...row, pending_request_id: pendingPaid ? summary?.paid?.pending_request_id : null, pending_request_status: summary?.paid?.pending_request_status })}
                        >
                          <td className="px-4 py-2.5 font-medium">{row.shipment_id}</td>
                          <td className="px-4 py-2.5 text-gray-600">{formatIndianDate(row.booking_date)}</td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {[row.sender_location, row.receiver_location].filter(Boolean).join(' → ') || '—'}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.paid_amount)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-green-700">
                            {fullCurrency(row.paid_remitted_amount)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-red-700">
                            {fullCurrency(row.amount_outstanding)}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600 capitalize">{row.paid_remit_status || 'pending'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="xl:hidden space-y-3 p-3">
                  {paidShipments.map((row) => (
                    <button
                      key={`card-${row.shipment_id}`}
                      type="button"
                      className="w-full text-left rounded-xl border border-gray-100 border-l-4 border-l-amber-400 bg-white p-3.5 shadow-sm"
                      onClick={() => openDue('paid', { ...row, pending_request_id: pendingPaid ? summary?.paid?.pending_request_id : null, pending_request_status: summary?.paid?.pending_request_status })}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm break-all min-w-0">{row.shipment_id}</p>
                        <p className="tabular-nums font-semibold text-red-700 shrink-0">{fullCurrency(row.amount_outstanding)}</p>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {formatIndianDate(row.booking_date)} · {[row.sender_location, row.receiver_location].filter(Boolean).join(' → ') || '—'}
                      </p>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                        <span>Paid <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.paid_amount)}</span></span>
                        <span>Remitted <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.paid_remitted_amount)}</span></span>
                      </div>
                    </button>
                  ))}
                </div>
                </>
              )}
            </div>
          )}

          {listTab === 'to_pay' && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">To-Pay remittance due</h2>
                {toPayGross > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Net remittance: To-Pay {fullCurrency(toPayGross)} − commission {fullCurrency(commissionTakenOff)}
                    {' = '}
                    {fullCurrency(roundMoney(Math.max(0, toPayGross - commissionTakenOff)))}.
                    {' '}
                    Still to return (open): {fullCurrency(twoPDue)}
                    {branchExpense > 0 ? ` − approved expenses ${fullCurrency(branchExpense)}` : ''}.
                  </p>
                )}
              </div>
              {!toPayItems.length ? (
                <p className="text-sm text-gray-400 text-center py-8">No outstanding To-Pay MLs</p>
              ) : (
                <>
                <div className="hidden xl:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-semibold">ML Sheet</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Location</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Total freight</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Commission</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Net due</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Returned</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Outstanding</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {toPayItems.map((row) => (
                        <tr
                          key={`${row.ml_sheet_id}-${row.agent_user_id}`}
                          className="hover:bg-sky-50/50 cursor-pointer"
                          onClick={() => openDue('to_pay', row)}
                        >
                          <td className="px-4 py-2.5 font-medium">{row.ml_sheet_id}</td>
                          <td className="px-4 py-2.5 text-gray-600">{formatIndianDate(row.created_at)}</td>
                          <td className="px-4 py-2.5 text-gray-600">{row.location_name || '—'}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.total_freight)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                            {row.commission_pct != null ? `${row.commission_pct}% · ` : ''}
                            {fullCurrency(
                              row.net_amount != null ? row.net_amount : row.commission_amount,
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.amount_due)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-green-700">
                            {fullCurrency(row.amount_returned)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-red-700">
                            {fullCurrency(row.amount_outstanding)}
                          </td>
                        </tr>
                      ))}
                      {(summary?.branch_expense_rows || []).map((row) => (
                        <tr key={row.expense_id} className="bg-blue-50/40">
                          <td className="px-4 py-2.5 font-medium">{row.expense_id}</td>
                          <td className="px-4 py-2.5 text-gray-600">{formatIndianDate(row.expense_date)}</td>
                          <td className="px-4 py-2.5 text-gray-600">
                            Approved expense · {row.category}
                            {row.description ? ` — ${row.description}` : ''}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-400">—</td>
                          <td className="px-4 py-2.5 text-right text-gray-400">—</td>
                          <td className="px-4 py-2.5 text-right text-gray-400">—</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-blue-800">{fullCurrency(row.amount)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-blue-800">−{fullCurrency(row.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="xl:hidden space-y-3 p-3">
                  {toPayItems.map((row) => (
                    <button
                      key={`card-${row.ml_sheet_id}-${row.agent_user_id}`}
                      type="button"
                      className="w-full text-left rounded-xl border border-gray-100 border-l-4 border-l-sky-400 bg-white p-3.5 shadow-sm"
                      onClick={() => openDue('to_pay', row)}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm break-all min-w-0">{row.ml_sheet_id}</p>
                        <p className="tabular-nums font-semibold text-red-700 shrink-0">{fullCurrency(row.amount_outstanding)}</p>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {formatIndianDate(row.created_at)} · {row.location_name || '—'}
                      </p>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
                        <span>Freight <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.total_freight)}</span></span>
                        <span>
                          Commission{' '}
                          <span className="tabular-nums font-medium text-gray-900">
                            {fullCurrency(row.net_amount != null ? row.net_amount : row.commission_amount)}
                          </span>
                        </span>
                        <span>Net due <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.amount_due)}</span></span>
                        <span>Returned <span className="tabular-nums font-medium text-gray-900">{fullCurrency(row.amount_returned)}</span></span>
                      </div>
                    </button>
                  ))}
                  {(summary?.branch_expense_rows || []).map((row) => (
                    <div
                      key={`card-${row.expense_id}`}
                      className="rounded-xl border border-blue-100 border-l-4 border-l-blue-400 bg-blue-50/40 p-3.5"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-sm break-all min-w-0">{row.expense_id}</p>
                        <p className="tabular-nums font-semibold text-blue-800 shrink-0">−{fullCurrency(row.amount)}</p>
                      </div>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        {formatIndianDate(row.expense_date)} · Approved expense · {row.category}
                        {row.description ? ` — ${row.description}` : ''}
                      </p>
                    </div>
                  ))}
                </div>
                </>
              )}
            </div>
          )}

          {listTab === 'company_paid' && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">Company paid you</h2>
                <p className="text-xs text-gray-500 mt-1">
                  Money the company sent you from Settlement Approvals. This is not a receipt you upload.
                </p>
              </div>
              {!companyPayments.length ? (
                <p className="text-sm text-gray-400 text-center py-10">No company payment in this period</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-semibold">Payment</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Mode</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Paid by</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Proof</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {companyPayments.map((row) => (
                        <Fragment key={row.expense_id}>
                          <tr>
                            <td className="px-4 py-2.5 font-mono text-xs">{row.expense_id}</td>
                            <td className="px-4 py-2.5 text-gray-600">{formatIstDateTime(row.payment_date) || formatIndianDate(row.expense_date)}</td>
                            <td className="px-4 py-2.5 text-gray-600 capitalize">{row.payment_mode || '—'}</td>
                            <td className="px-4 py-2.5 text-gray-600">{row.paid_by || '—'}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-emerald-800">{fullCurrency(row.amount)}</td>
                            <td className="px-4 py-2.5 text-right">
                              {row.attachment_url ? (
                                <button
                                  type="button"
                                  className="text-sky-800 underline text-xs font-semibold"
                                  onClick={() => setProofView(row)}
                                >
                                  View proof
                                </button>
                              ) : '—'}
                            </td>
                          </tr>
                          {(row.lines || []).length > 0 ? (
                            <tr>
                              <td colSpan={6} className="px-4 pb-3 pt-0">
                                <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">
                                  {(row.lines || []).map((line) => (
                                    <div key={line.id || line.ml_sheet_id} className="flex items-center justify-between gap-3 text-xs text-gray-600 py-1">
                                      <span>{line.ml_sheet_id || 'Record'}</span>
                                      <span className="tabular-nums font-medium text-gray-800">{fullCurrency(line.amount)}</span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {listTab === 'requests' && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-semibold text-gray-800">My requests</h2>
              </div>
              {!batches.length ? (
                <p className="text-sm text-gray-400 text-center py-10">No settlement requests yet</p>
              ) : (
                <>
                <div className="hidden xl:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-semibold">Batch</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                        <th className="text-left px-4 py-2.5 font-semibold">AI review</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Paid</th>
                        <th className="text-right px-4 py-2.5 font-semibold">To-Pay</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Total</th>
                        <th className="text-left px-4 py-2.5 font-semibold">Submitted</th>
                        <th className="text-right px-4 py-2.5 font-semibold">View</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {batches.map((row) => {
                        const ocrStatus = (row.paid_requests?.[0] || row.to_pay_requests?.[0])?.ocr_status;
                        return (
                        <tr
                          key={row.batch_id}
                          className="hover:bg-sky-50/50 cursor-pointer"
                          onClick={() => openView(row)}
                        >
                          <td className="px-4 py-2.5 font-mono text-xs">
                            <button type="button" className="underline" onClick={(e) => { e.stopPropagation(); openView(row); }}>
                              {row.batch_id}
                            </button>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs ${statusBadge(row.status)}`}>
                              {statusLabel(row.status)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-gray-600">
                            {row.status === 'OCR_PENDING' ? 'AI reading…' : aiReviewLabel(ocrStatus)}
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.paid_amount)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(row.to_pay_amount)}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fullCurrency(row.total_amount)}</td>
                          <td className="px-4 py-2.5 text-gray-500">{formatIstDateTime(row.submitted_at)}</td>
                          <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                            <Button variant="secondary" size="sm" onClick={() => openView(row)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="xl:hidden space-y-3 p-3">
                  {batches.map((row) => {
                    const ocrStatus = (row.paid_requests?.[0] || row.to_pay_requests?.[0])?.ocr_status;
                    return (
                      <button
                        key={`card-${row.batch_id}`}
                        type="button"
                        className="w-full text-left rounded-xl border border-gray-100 border-l-4 border-l-blue-500 bg-white p-3.5 shadow-sm"
                        onClick={() => openView(row)}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <span className={`${listIdLinkMobileClass} break-all`}>{row.batch_id}</span>
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold shrink-0 ${statusBadge(row.status)}`}>
                            {statusLabel(row.status)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500 block">Paid</span>
                            <p className="tabular-nums font-medium">{fullCurrency(row.paid_amount)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 block">To-Pay</span>
                            <p className="tabular-nums font-medium">{fullCurrency(row.to_pay_amount)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 block">Total</span>
                            <p className="tabular-nums font-semibold">{fullCurrency(row.total_amount)}</p>
                          </div>
                          <div>
                            <span className="text-gray-500 block">Submitted</span>
                            <p className="font-medium text-gray-800">{formatIstDateTime(row.submitted_at)}</p>
                          </div>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-2">
                          {row.status === 'OCR_PENDING' ? 'AI reading…' : aiReviewLabel(ocrStatus)}
                        </p>
                      </button>
                    );
                  })}
                </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {showForm ? (
        <SettlementModalShell
          title="Make settlement"
          subtitle="Upload the receipt for the same amount shown on the dispatch sheet. Cashbook posts after the accountant approves."
          onClose={() => setShowForm(false)}
          footer={(
            <div className="flex justify-end gap-2 flex-wrap">
              <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button variant="primary" disabled={submitting} onClick={onSubmit}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Submit settlement
              </Button>
            </div>
          )}
        >
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3">
              <p className="text-xs font-semibold text-emerald-800 mb-2">Amount to settle with company</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-700">
                {showPaid && pasDue > 0 && (
                  <>
                    <span>Paid due</span>
                    <span className="text-right tabular-nums">{fullCurrency(pasDue)}</span>
                  </>
                )}
                <span>To-Pay</span>
                <span className="text-right tabular-nums">{fullCurrency(toPayGross || twoPDue)}</span>
                <span>Commission</span>
                <span className="text-right tabular-nums">{fullCurrency(commissionTakenOff)}</span>
                <span>Approved expenses</span>
                <span className="text-right tabular-nums">{fullCurrency(branchExpense)}</span>
                <span>Amount to return</span>
                <span className="text-right tabular-nums">{fullCurrency(Math.max(0, net))}</span>
              </div>
              {toPayItems.length > 0 ? (
                <div className="mt-2 pt-2 border-t border-emerald-100">
                  <p className="text-[11px] font-semibold text-emerald-800 mb-1">
                    ML sheets in this settlement ({toPayItems.length})
                  </p>
                  <ul className="space-y-0.5 text-[11px] text-gray-600">
                    {toPayItems.map((row) => (
                      <li key={`${row.ml_sheet_id}-${row.agent_user_id}`} className="flex justify-between gap-2">
                        <span className="break-all">{row.ml_sheet_id}</span>
                        <span className="tabular-nums shrink-0">
                          {Number(row.amount_outstanding) > 0.009
                            ? fullCurrency(row.amount_outstanding)
                            : '₹0 (commission covered)'}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[10px] text-gray-500 mt-1">
                    Click any ML on the To-Pay list to see your branch shipment table before submitting.
                  </p>
                </div>
              ) : null}
              <div className="mt-2 pt-2 border-t border-emerald-200 flex items-center justify-between">
                <span className="text-xs font-semibold text-emerald-800">You remit to company</span>
                <span className="text-base font-bold text-emerald-900 tabular-nums">{fullCurrency(cashRemit)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs text-gray-600">
                <span>You send now</span>
                <span className="tabular-nums font-medium">
                  {fullCurrency(
                    (paidDisabled ? 0 : (parseFloat(paidAmount) || 0))
                    + (toPayDisabled ? 0 : (parseFloat(toPayAmount) || 0)),
                  )}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {showPaid && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Paid amount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    className={`${formInputClass()} ${paidDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                    disabled={paidDisabled}
                    placeholder={pasDue <= 0 ? 'No Paid due' : undefined}
                  />
                  {pasDue <= 0 ? (
                    <p className="text-xs text-gray-500 mt-1">No Paid due — this field is disabled.</p>
                  ) : pendingPaid ? (
                    <p className="text-xs text-amber-700 mt-1">A Paid settlement is already pending review.</p>
                  ) : null}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Approved expenses</label>
                <input
                  type="text"
                  value={fullCurrency(branchExpense)}
                  readOnly
                  disabled
                  className={`${formInputClass()} bg-gray-100 text-gray-500 cursor-not-allowed`}
                />
                <p className="text-xs text-gray-500 mt-1">Already minus from the amount you return. This is not entered again.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">To-Pay amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                    value={toPayAmount}
                  onChange={(e) => setToPayAmount(e.target.value)}
                  className={`${formInputClass()} ${toPayDisabled ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                  disabled={toPayDisabled}
                  placeholder={twoPDue <= 0 ? 'No To-Pay due' : undefined}
                />
                {twoPDue <= 0 ? (
                  <p className="text-xs text-gray-500 mt-1">No To-Pay due — this field is disabled.</p>
                ) : pendingTwoP ? (
                  <p className="text-xs text-amber-700 mt-1">A To-Pay settlement is already pending review.</p>
                ) : null}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment mode</label>
                <select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className={formSelectClass()}
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reference</label>
                <input
                  type="text"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  className={formInputClass()}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
              <input
                type="text"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                className={formInputClass()}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Receipt screenshot</label>
              <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-xl px-3 py-6 text-sm text-gray-600 cursor-pointer hover:bg-gray-50 justify-center">
                <Upload className="w-4 h-4" />
                {receipt ? receipt.name : 'Upload JPG / PNG'}
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFileChange} />
              </label>
              <p className="text-xs text-gray-500 mt-1">
                Paying both together skips AI amount-match (one receipt vs combined total) and goes to accountant review. Submit does not post cashbook.
              </p>
            </div>
          </form>
        </SettlementModalShell>
      ) : null}

      {viewDue ? (
        <CompanySettlementDueView
          kind={viewDue.kind}
          row={viewDue.row}
          canSettle={canSettle}
          onSettle={() => {
            setViewDue(null);
            openSettle();
          }}
          onViewPending={(pendingId) => openView(pendingId)}
          onClose={() => setViewDue(null)}
        />
      ) : null}

      {viewBatch ? (
        <CompanySettlementBatchView
          batch={viewBatch}
          dueHints={toPayItems}
          onClose={() => setViewBatch(null)}
        />
      ) : null}

      {proofView ? (
        <PaymentProofModal
          url={proofView.attachment_url}
          title={proofView.expense_id || 'Payment proof'}
          onClose={() => setProofView(null)}
        />
      ) : null}
    </DashboardLayout>
  );
};

export default AgentCompanySettlementPage;
